import { LoggingPopupComponent } from './components/logging-popup/logging-popup.component';
import { BrowserModule } from '@angular/platform-browser';
import { Component, NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AppRoutingModule } from './app.routing.module';
import { AppComponent } from './app.component';
import { AppGlobals } from './app.globals';
import { ApiBaseService } from 'src/app/services/api-base.service';
import { AuthService } from 'src/app/services/auth.service';
import { LogoutPage } from './pages/logout/logout.page';
import { ErrorFeedbackComponent } from './components/feedback-error/error-feedback-component';
import { AuthenticationInterceptor } from './shared/interceptors/authentication.interceptor';
import { RouteReuseStrategy } from '@angular/router';
import { TabRouteReuseStrategy } from './shared/tab-route-reuse.strategy';
import { ContractFileViewerComponent } from './components/file-viewer-overlay/file-viewer-overlay.component';

// Components
import { AppLoginComponent } from './components/app-login/app-login.component';
import { PopUpComponent } from './components/pop-up/pop-up.component';
import { AppLoaderComponent } from './components/app-global-loader/app-global-loader.component';
import { LandingPage } from './pages/grid-connection-list/landing/landing.page';

import { MarkdownModule } from 'ngx-markdown';
import { HttpClient, withXhr } from '@angular/common/http';

// Third Party
import {
  DxButtonModule,
  DxFormModule,
  DxMenuModule,
  DxToolbarModule,
  DxDataGridModule,
  DxTabPanelModule,
  DxRadioGroupModule,
  DxDrawerModule,
  DxListModule,
  DxLoadPanelModule,
  DxToastModule,
  DxTemplateModule,
  DxPopupModule,
  DxPopoverModule,
  DxDropDownButtonModule,
  DxDateBoxModule,
  DxCalendarModule,
  DxCheckBoxModule,
  DxScrollViewModule,
  DxSelectBoxModule,
  DxDropDownBoxModule,
  DxTreeViewModule,
  DxFileUploaderModule,
  DxNumberBoxModule,
} from 'devextreme-angular';

/* MSAL */
// Import MSAL and MSAL browser libraries.
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalModule,
  MsalRedirectComponent,
  MsalService,
} from '@azure/msal-angular';
import {
  IPublicClientApplication,
  InteractionType,
  PublicClientApplication,
  RedirectRequest,
} from '@azure/msal-browser';

// Import the Azure AD B2C configuration
import { loginRequest, msalConfig, protectedResources } from './auth-config';

// Import the Angular HTTP interceptor.
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { APP_INITIALIZER } from '@angular/core';
import { IconRegistryService } from './services/icon-registry.service';
import { HeaderComponent } from './layout/app-header/app-header.component';
import { DrawerComponent } from './layout/app-drawer/app-drawer.component';
import { TimelineComponent } from './pages/config/trafficlight-timeline/timeline-component';
import { GlobalPopupComponent } from './components/generic-popup/presets/global-popup/global-popup.component';
import { loadAppVersion } from './version.initializer';

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication(msalConfig);
}

/**
 * Set your default interaction type for MSALGuard here. If you have any
 * additional scopes you want the user to consent upon login, add them here as well.
 */
export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: loginRequest,
  };
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map([
    [protectedResources.projectsApi.endpoint, protectedResources.projectsApi.scopes],
    [protectedResources.graphApi.endpoint, protectedResources.graphApi.scopes],
  ]);
  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function initializeIconRegistry(iconRegistry: IconRegistryService): () => Promise<void> {
  return () => iconRegistry.initializeIcons();
}

@NgModule({
  declarations: [AppComponent, LogoutPage, AppLoginComponent],
  bootstrap: [AppComponent, MsalRedirectComponent],
  imports: [
    AppLoaderComponent,
    ErrorFeedbackComponent,
    PopUpComponent,
    BrowserModule,
    AppRoutingModule,
    DxFormModule,
    DxToolbarModule,
    DxMenuModule,
    DxToastModule,
    DxLoadPanelModule,
    DxButtonModule,
    DxTemplateModule,
    DxPopupModule,
    DxDropDownButtonModule,
    DxPopoverModule,
    DxDataGridModule,
    DxTabPanelModule,
    DxRadioGroupModule,
    DxDrawerModule,
    DxListModule,
    LandingPage,
    DxCalendarModule,
    DxDateBoxModule,
    DxCheckBoxModule,
    DxScrollViewModule,
    DxSelectBoxModule,
    DxDropDownBoxModule,
    DxTreeViewModule,
    DxFileUploaderModule,
    DxNumberBoxModule,
    DxScrollViewModule,
    LoggingPopupComponent,
    // Initiate the MSAL library with the MSAL configuration object
    MsalModule,
    HeaderComponent,
    DrawerComponent,
    GlobalPopupComponent,
    ContractFileViewerComponent,
    /* Changes end here. */
    TimelineComponent,
    // CookieBannerComponent removed — see the comment in app.component.html.
    MarkdownModule.forRoot({ loader: HttpClient }),
  ],
  providers: [
    AppGlobals,
    ApiBaseService,
    AuthService,
    { provide: RouteReuseStrategy, useClass: TabRouteReuseStrategy },
    { provide: 'windowObject', useValue: window },
    /* Changes start here. */
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthenticationInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    /* Changes end here. */
    {
      provide: APP_INITIALIZER,
      useFactory: initializeIconRegistry,
      deps: [IconRegistryService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: loadAppVersion,
      deps: [HttpClient],
      multi: true,
    },
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
  ],
})
export class AppModule {}
